import type { CloudPC } from '../types';

interface CloudPCTableProps {
  data: CloudPC[];
  selectedIds: string[];
  onSelectChange: (ids: string[]) => void;
  onViewDetail: (id: string) => void;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
  onRestart: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function CloudPCTable({
  data,
  selectedIds,
  onSelectChange,
  onViewDetail,
  onStart,
  onStop,
  onRestart,
  onDelete,
}: CloudPCTableProps) {
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectChange(data.map((item) => item.id));
    } else {
      onSelectChange([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      onSelectChange([...selectedIds, id]);
    } else {
      onSelectChange(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const getStatusConfig = (status: CloudPC['status']) => {
    const configs = {
      running: { text: '运行中', color: 'bg-green-100 text-green-700', icon: 'ri-play-circle-fill' },
      stopped: { text: '已停止', color: 'bg-gray-100 text-gray-700', icon: 'ri-stop-circle-fill' },
      creating: { text: '创建中', color: 'bg-blue-100 text-blue-700', icon: 'ri-loader-4-line' },
      fault: { text: '故障', color: 'bg-red-100 text-red-700', icon: 'ri-error-warning-fill' },
      expired: { text: '已过期', color: 'bg-orange-100 text-orange-700', icon: 'ri-time-fill' },
    };
    return configs[status];
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('已复制到剪贴板');
  };

  const getUsageColor = (usage: number) => {
    if (usage >= 90) return 'bg-red-500';
    if (usage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.length === data.length && data.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                云电脑ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                云电脑名称
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                所属用户
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                配置规格
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                操作系统
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                当前状态
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                购买方式
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                到期时间
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                剩余时长
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                IP地址
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                资源使用率
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                创建时间
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                所属机房
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                所属服务器
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((item) => {
              const statusConfig = getStatusConfig(item.status);
              const isExpiringSoon = item.remainingTime.includes('小时') && !item.remainingTime.includes('天');

              return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{item.id}</span>
                      <button
                        onClick={() => copyToClipboard(item.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-gray-500">{item.productName}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="text-gray-900">{item.userName}</div>
                      <div className="text-gray-500">{item.userPhone}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">
                      <div>CPU: {item.cpu}核</div>
                      <div>内存: {item.memory}GB</div>
                      <div>系统盘: {item.systemDisk}GB</div>
                      {item.gpu && <div>GPU: {item.gpu}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">{item.os}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color} whitespace-nowrap`}
                    >
                      <i className={statusConfig.icon}></i>
                      {statusConfig.text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 whitespace-nowrap">
                      {item.purchaseDetail}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 whitespace-nowrap">
                      {item.expireTime}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm whitespace-nowrap ${
                        isExpiringSoon ? 'text-red-600 font-medium' : 'text-gray-900'
                      }`}
                    >
                      {item.remainingTime}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {item.status === 'running' ? (
                      <div className="text-sm text-gray-900">
                        <div>内网: {item.internalIp}</div>
                        <div>公网: {item.publicIp}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {item.cpuUsage !== undefined ? (
                      <div className="space-y-1 min-w-[120px]">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-10">CPU</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(item.cpuUsage)}`}
                              style={{ width: `${item.cpuUsage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-900 w-8">{item.cpuUsage}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-10">内存</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(item.memoryUsage!)}`}
                              style={{ width: `${item.memoryUsage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-900 w-8">{item.memoryUsage}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-10">磁盘</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(item.diskUsage!)}`}
                              style={{ width: `${item.diskUsage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-900 w-8">{item.diskUsage}%</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 whitespace-nowrap">
                      {item.createTime}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 whitespace-nowrap">
                      {item.datacenter}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 whitespace-nowrap">{item.server}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onViewDetail(item.id)}
                        className="text-blue-600 hover:text-blue-700 text-sm whitespace-nowrap"
                      >
                        详情
                      </button>
                      {item.status === 'running' && (
                        <>
                          <button
                            onClick={() => onStop(item.id)}
                            className="text-orange-600 hover:text-orange-700 text-sm whitespace-nowrap"
                          >
                            停止
                          </button>
                          <button
                            onClick={() => onRestart(item.id)}
                            className="text-purple-600 hover:text-purple-700 text-sm whitespace-nowrap"
                          >
                            重启
                          </button>
                        </>
                      )}
                      {item.status === 'stopped' && (
                        <button
                          onClick={() => onStart(item.id)}
                          className="text-green-600 hover:text-green-700 text-sm whitespace-nowrap"
                        >
                          启动
                        </button>
                      )}
                      {item.status === 'fault' && (
                        <>
                          <button className="text-yellow-600 hover:text-yellow-700 text-sm whitespace-nowrap">
                            诊断
                          </button>
                          <button
                            onClick={() => onRestart(item.id)}
                            className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                          >
                            强制重启
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => onDelete(item.id)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
